import * as THREE from 'three';

interface AsciiEffectOptions {
  resolution?: number;
  color?: boolean;
  invert?: boolean;
  canvas?: HTMLCanvasElement;
}

/**
 * Fast canvas-based ASCII effect with Matrix-style character cycling.
 * Characters animate within brightness tiers — same density, different glyphs each frame.
 */
export class AsciiEffect {
  domElement: HTMLCanvasElement;
  _bgCSS: string = '#000000';
  _cellBgCSS: string = '#000000';
  cursorX: number = -9999;
  cursorY: number = -9999;
  palette: string[] = ['#ff0000'];

  private renderer: THREE.WebGLRenderer;
  private resolution: number;
  private bColor: boolean;
  private bInvert: boolean;
  private cursorTrail: { x: number; y: number; age: number }[] = [];
  private readonly trailMaxAge = 100;   // frames until a trail point fully fades
  private readonly trailMaxLength = 20; // max simultaneous trail points
  private lastCursorX: number = -9999;
  private lastCursorY: number = -9999;

  // Brightness tiers — each tier is a pool of visually similar-density
  // characters, ordered from darkest (most empty) to brightest (most filled).
  private readonly glyphs =
    '!@#$%^&*()_+-=[]{}|;:,.<>?/~`ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  private readonly tiers: readonly string[] = [
    '·',                          // 0: empty
    '.\'`',                      // 1: very sparse
    ':-~"^',                      // 2: light
    '=+;!|',                      // 3: medium-light
    '?/\\(){}',                   // 4: medium
    '*#%&$',                      // 5: medium-heavy
    'XWMB@',                      // 6: heavy
    '█▓▒░',                       // 7: block chars (heaviest)
  ];

  private width = 0;
  private height = 0;
  private cols = 0;
  private rows = 0;
  private frameCount = 0;
  private bgCanvas!: HTMLCanvasElement;
  private bgCtx!: CanvasRenderingContext2D;

  private sampleCanvas: HTMLCanvasElement;
  private sampleCtx: CanvasRenderingContext2D;
  private outCtx: CanvasRenderingContext2D;

  // Per-cell random offset so cells don't all flip in sync
  private cellSeeds: Uint8Array | null = null;

  constructor(
    renderer: THREE.WebGLRenderer,
    options: AsciiEffectOptions = {}
  ) {
    this.renderer = renderer;
    this.resolution = options.resolution ?? 0.15;
    this.bColor = options.color ?? false;
    this.bInvert = options.invert ?? false;

    this.sampleCanvas = document.createElement('canvas');
    this.sampleCtx = this.sampleCanvas.getContext('2d')!;

    const outCanvas = options.canvas ?? document.createElement('canvas');
    this.domElement = outCanvas;
    this.outCtx = outCanvas.getContext('2d')!;
  }

  setSize(w: number, h: number): void {
    this.width = w;
    this.height = h;
    this.cols = Math.floor(w * this.resolution);
    this.rows = Math.floor(h * this.resolution);
    // Render the WebGL scene at sample resolution, not output resolution.
    // `false` prevents Three.js from setting CSS size on the renderer's canvas.
    this.renderer.setSize(this.cols, this.rows, false);
    this.sampleCanvas.width = this.cols;
    this.sampleCanvas.height = this.rows;
    this.domElement.width = w;
    this.domElement.height = h;
    // Generate random seed per cell for staggered cycling
    this.cellSeeds = new Uint8Array(this.cols * this.rows);
    for (let i = 0; i < this.cellSeeds.length; i++) {
      this.cellSeeds[i] = (Math.random() * 256) | 0;
    }
    this.bgCanvas = document.createElement('canvas');
    this.bgCanvas.width = w;
    this.bgCanvas.height = h;
    this.bgCtx = this.bgCanvas.getContext('2d')!;
    this.rebuildCellBg();
  }

  rebuildCellBg(): void {
    if (!this.bgCtx || !this.width) return;
    const cellW = this.width / this.cols, cellH = this.height / this.rows;
    const baseRadius = Math.max(cellW, cellH) * 0.6;
    this.bgCtx.clearRect(0, 0, this.width, this.height);
    this.bgCtx.fillStyle = this._cellBgCSS;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        this.bgCtx.beginPath();
        this.bgCtx.arc(x * cellW + cellW * 0.5, y * cellH + cellH * 0.5, baseRadius, 0, Math.PI * 2);
        this.bgCtx.fill();
      }
    }
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    const { renderer, sampleCtx, outCtx, cols, rows, width, height, tiers, bColor, bInvert } = this;
    const cellSeeds = this.cellSeeds;
    if (!cellSeeds) return;
    // Age existing trail points and drop the dead ones
    for (const p of this.cursorTrail) p.age++;
    this.cursorTrail = this.cursorTrail.filter(p => p.age < this.trailMaxAge);

    // Push the current cursor position if it moved (avoids piling up
    // identical points when the cursor is still)
    if (
      this.cursorX > -9000 &&
      (this.cursorX !== this.lastCursorX || this.cursorY !== this.lastCursorY)
    ) {
      this.cursorTrail.push({ x: this.cursorX, y: this.cursorY, age: 0 });
      if (this.cursorTrail.length > this.trailMaxLength) {
        this.cursorTrail.shift();
      }
      this.lastCursorX = this.cursorX;
      this.lastCursorY = this.cursorY;
    }
    renderer.render(scene, camera);
    sampleCtx.clearRect(0, 0, cols, rows);
    sampleCtx.drawImage(renderer.domElement, 0, 0);  // ← no scaling needed now
    const imgData = sampleCtx.getImageData(0, 0, cols, rows).data;

    outCtx.clearRect(0, 0, width, height);
    if (this._bgCSS && this._bgCSS !== 'transparent') {
      outCtx.fillStyle = this._bgCSS;
      outCtx.fillRect(0, 0, width, height);
    }

    const cellW = width / cols;
    const cellH = height / rows;

    // Blit the cached full-size bg grid in one call
    outCtx.drawImage(this.bgCanvas, 0, 0);

    // Only re-draw cells affected by the ripple, inside a small bounding box
    if (this.cursorX > -9000 && this._cellBgCSS && this._cellBgCSS !== 'transparent') {
      const CORE = 35, MID = 90, OUT = 140;
      const cellW = width / cols;
      const cellH = height / rows;
      const baseRadius = Math.max(cellW, cellH) * 0.6;

      // Bounding box around cursor at radius OUT
      const xStart = Math.max(0, Math.floor((this.cursorX - OUT) / cellW));
      const yStart = Math.max(0, Math.floor((this.cursorY - OUT) / cellH));
      const xEnd = Math.min(cols, Math.ceil((this.cursorX + OUT) / cellW));
      const yEnd = Math.min(rows, Math.ceil((this.cursorY + OUT) / cellH));

      // Clear the bbox region back to --accent, then redraw the grid cells in it
      outCtx.fillStyle = this._bgCSS;
      outCtx.fillRect(xStart * cellW, yStart * cellH,
        (xEnd - xStart) * cellW, (yEnd - yStart) * cellH);

      outCtx.fillStyle = this._cellBgCSS;
      for (let y = yStart; y < yEnd; y++) {
        for (let x = xStart; x < xEnd; x++) {
          const a = imgData[(y * cols + x) * 4 + 3];
          const hasContent = a >= 10;

          const cx = x * cellW + cellW * 0.5;
          const cy = y * cellH + cellH * 0.5;

          let scale = 1;
          if (!hasContent) {
            const dx = cx - this.cursorX;
            const dy = cy - this.cursorY;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < CORE) scale = 1;
            else if (d < MID) scale = 1 - ((d - CORE) / (MID - CORE)) * 0.2;
            else if (d < OUT) scale = 0.8 + ((d - MID) / (OUT - MID)) * 0.2;
          }

          outCtx.beginPath();
          outCtx.arc(cx, cy, baseRadius * scale, 0, Math.PI * 2);
          outCtx.fill();
        }
      }
    }

    const fontSize = Math.max(cellH * 0.95, 6);
    outCtx.font = `bold ${fontSize}px ui-monospace, "Courier New", monospace`;
    outCtx.textBaseline = 'middle';
    outCtx.textAlign = 'center';

    this.frameCount++;

    // Cycle speed: change character every N frames (staggered per cell)
    const cycleSpeed = 4;
    const numTiers = tiers.length;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const idx = y * cols + x;
        const i = idx * 4;
        const r = imgData[i];
        const g = imgData[i + 1];
        const b = imgData[i + 2];
        const a = imgData[i + 3];
        if (a < 10) continue; // skip transparent background

        let brightness = (0.3 * r + 0.59 * g + 0.11 * b) / 255;
        if (bInvert) brightness = 1 - brightness;
        if (brightness < 0.02) continue;

        // Map brightness to tier
        const tierIdx = Math.min(numTiers - 1, Math.floor(brightness * numTiers));
        const tier = tiers[tierIdx];
        if (tier === ' ') continue;

        // Pick character from tier — cycles over time, staggered per cell
        const seed = cellSeeds[idx];
        const cycle = ((this.frameCount + seed) / cycleSpeed) | 0;
        const ch = tier[cycle % tier.length];

        // Compute the base (non-holo) color first
        let fillStyle: string;
        if (bColor) {
          fillStyle = `rgb(${r},${g},${b})`;
        } else {
          const v = Math.floor(brightness * 255);
          fillStyle = `rgb(${v},${v},${v})`;
        }

        outCtx.fillStyle = fillStyle;

        outCtx.fillText(ch, x * cellW + cellW * 0.5, y * cellH + cellH * 0.5);
      }
    }

    // Cursor aura: paint holo glyphs on empty cells near the cursor
    // Cursor aura: torch flame that trails behind cursor motion
    if (this.cursorTrail.length > 0) {
      const TORCH = 120;
      const TORCH_SQ = TORCH * TORCH;   // ← add this line
      const MAX_SCALE = 4;
      const baseFontSize = Math.max(cellH * 0.95, 6);
      // Bounding box over all trail points
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of this.cursorTrail) {
        if (p.x - TORCH < minX) minX = p.x - TORCH;
        if (p.y - TORCH < minY) minY = p.y - TORCH;
        if (p.x + TORCH > maxX) maxX = p.x + TORCH;
        if (p.y + TORCH > maxY) maxY = p.y + TORCH;
      }
      const xStart = Math.max(0, Math.floor(minX / cellW));
      const yStart = Math.max(0, Math.floor(minY / cellH));
      const xEnd = Math.min(cols, Math.ceil(maxX / cellW));
      const yEnd = Math.min(rows, Math.ceil(maxY / cellH));
      const buckets: Array<Array<{ ch: string; color: string; cx: number; cy: number }>> =
        [[], [], [], [], [], []];
      for (let y = yStart; y < yEnd; y++) {
        for (let x = xStart; x < xEnd; x++) {
          const idx = y * cols + x;
          const a = imgData[idx * 4 + 3];
          if (a >= 10) continue;

          const cx = x * cellW + cellW * 0.5;
          const cy = y * cellH + cellH * 0.5;

          // Find the strongest heat contribution from any trail point.
          // Older points contribute less via the age fade.
          let heat = 0;
          for (const p of this.cursorTrail) {
            const dx = cx - p.x;
            const dy = cy - p.y;
            const d2 = dx * dx + dy * dy;
            if (d2 >= TORCH_SQ) continue;
            const d = Math.sqrt(d2);

            const distFade = 1 - d / TORCH;           // 1 at point, 0 at edge
            const ageFade = 1 - p.age / this.trailMaxAge; // 1 fresh, 0 dying
            const contribution = distFade * distFade * ageFade;

            if (contribution > heat) heat = contribution;
          }

          if (heat < 0.02) continue;

          // Glyph cycling staggered per cell
          const seed = cellSeeds[idx];
          const cycle = ((this.frameCount + seed) / 3) | 0;
          const ch = this.glyphs[cycle % this.glyphs.length];

          const color =
            this.palette[(cycle + seed) % this.palette.length] || 'transparent';

          const bucketIdx = Math.min(5, Math.floor(heat * 6));
          buckets[bucketIdx].push({ ch, color, cx, cy });
        }
      }
      for (let b = 0; b < 6; b++) {
        if (buckets[b].length === 0) continue;
        const heatMid = (b + 0.5) / 6;
        const size = baseFontSize * (0.3 + heatMid * (MAX_SCALE - 0.3));
        outCtx.font = `bold ${size}px "VT323", ui-monospace, monospace`;
        for (const c of buckets[b]) {
          outCtx.fillStyle = c.color;
          outCtx.fillText(c.ch, c.cx, c.cy);
        }
      }
    }
  }
}
